import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { User, LogOut, X } from 'lucide-react';
import { UserChip } from './UserChip';

/**
 * Mobile-only account tab + slide-up sheet.
 *
 * Bottom nav on mobile hides the desktop `.side .userchip / .signout`,
 * leaving users with no way to see which role they are signed in as or
 * to sign out. This component fills that gap on viewports ≤ 720px:
 *   • A single `.nav`-styled Account tab appended to the bottom strip.
 *   • Tap → slide-up sheet with role pill, name, email and sign-out.
 *
 * On desktop it renders nothing (the sidebar already carries this UI).
 */
export const MobileAccountSheet = ({
  profile,
  email,
  onSignOut,
  active,
  onOpen,
  onClose,
  testIdPrefix = 'mobile-account',
}) => {
  // Lock body scroll while the sheet is open so background pages don't
  // scroll under the finger on iOS Safari.
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [active, onClose]);

  return (
    <>
      {/* Bottom-nav tab — the CSS `.side .nav.mobile-only-tab` rules hide
          it on desktop and style it identically to the other nav tabs. */}
      <button
        type="button"
        className={`nav mobile-only-tab ${active ? 'on' : ''}`}
        onClick={onOpen}
        data-testid={`${testIdPrefix}-tab`}
        aria-label="Account menu"
      >
        <span className="nav-icon-wrap">
          <User className="i" size={17} />
        </span>
        Account
      </button>

      {active && createPortal(
        <>
          <div
            className="mas-backdrop"
            onClick={onClose}
            data-testid={`${testIdPrefix}-backdrop`}
          />
          <div
            className="mas-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Account menu"
            data-testid={`${testIdPrefix}-sheet`}
          >
            <div className="mas-grip" aria-hidden="true" />
            <button
              type="button"
              className="mas-close"
              onClick={onClose}
              aria-label="Close account menu"
              data-testid={`${testIdPrefix}-close`}
            >
              <X size={18} />
            </button>

            <div className="mas-header">Signed in</div>
            <div className="mas-chip-wrap">
              <UserChip profile={profile} email={email} />
            </div>

            <Link
              to="/account"
              className="mas-action"
              onClick={onClose}
              data-testid={`${testIdPrefix}-goto-account`}
            >
              <User size={16} /> My account
            </Link>
            <button
              type="button"
              className="mas-action mas-danger"
              onClick={() => { onClose(); onSignOut(); }}
              data-testid={`${testIdPrefix}-signout`}
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default MobileAccountSheet;
